import jsPDF from 'jspdf';
import { AnalysisInputSummary } from '../components/ATypicalModule';
import { ensureRaleway } from '../src/pdf/fonts/raleway';
/* ======================================================
   PDF Formatting Constants (OSFN)
   ====================================================== */

const BRAND_ORANGE = '#F75C03';
const BRAND_GREY = '#4D4D4D';
const TEXT_COLOR = BRAND_GREY;
const LIGHT_GREY = '#F5F5F5';
const FOOTER_GREY = '#8C8C8C';

const ptToMm = (pt: number) => pt * (25.4 / 72);

const H1_SIZE = 18;
const H2_SIZE = 14;
const BODY_SIZE = 11;
const FOOTER_SIZE = 8;

const LINE_HEIGHT_FACTOR = 1.35;
const PARA_SPACING = ptToMm(6);
const MARGIN = 25;

const HEADER_TOP = 10;
const HEADER_H = 28; // reserved header height on every page
const BODY_START_Y = HEADER_TOP + HEADER_H + 8;

/* ======================================================
   Module-level variables
   ====================================================== */

let yCursor = 0;
let pageHeight: number;
let pageWidth: number;
let doc: jsPDF;
let FONT_FAMILY: 'Raleway' | 'helvetica' = 'helvetica';


let cachedBrandLogoDataUrl: string | null = null;
let cachedTenantLogoDataUrl: string | null = null;

/* ======================================================
   Helpers
   ====================================================== */

function hexToRgb(hex: string) {
  const h = String(hex || '').replace('#', '').trim();
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(full, 16);
  return {
    r: (n >> 16) & 255,
    g: (n >> 8) & 255,
    b: n & 255,
  };
}

function setFill(docRef: jsPDF, hex: string) {
  const { r, g, b } = hexToRgb(hex);
  docRef.setFillColor(r, g, b);
}

function setDraw(docRef: jsPDF, hex: string) {
  const { r, g, b } = hexToRgb(hex);
  docRef.setDrawColor(r, g, b);
}

function setText(docRef: jsPDF, hex: string) {
  const { r, g, b } = hexToRgb(hex);
  docRef.setTextColor(r, g, b);
}

function setFontConsistent(docRef: jsPDF) {
  // If you later embed Raleway via addFileToVFS/addFont, this will use it.
  // Otherwise it falls back once to helvetica and never switches again.
  try {
    docRef.setFont('Raleway', 'normal');
  } catch {
    docRef.setFont('helvetica', 'normal');
  }
}

async function loadImageAsDataUrl(urlOrDataUrl: string): Promise<string> {
  const s = String(urlOrDataUrl || '').trim();
  if (!s) throw new Error('empty_image');
  if (s.startsWith('data:image/')) return s;

  const res = await fetch(s, { mode: 'cors' });
  if (!res.ok) throw new Error(`logo_fetch_failed:${res.status}`);

  const blob = await res.blob();
  return await new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ''));
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}
function safeAddImage(docRef: jsPDF, dataUrl: string, x: number, y: number, w: number, h: number) {
  try {
    const s = String(dataUrl || '');
    if (!s.startsWith('data:image/')) return;

    const isPng = s.startsWith('data:image/png');
    const isJpg = s.startsWith('data:image/jpeg') || s.startsWith('data:image/jpg');
    const fmt = isPng ? 'PNG' : isJpg ? 'JPEG' : null;
    if (!fmt) return;

    docRef.addImage(s, fmt as any, x, y, w, h, undefined, 'FAST');
  } catch {
    // best-effort: ignore
  }
}

/* ======================================================
   Header (brandLogo left, tenantLogo right)
   ====================================================== */

async function drawHeader(
  pageNo: number,
  reportTitle: string,
  brandLogo: string,
  tenantLogoUrl?: string
) {
  const mx = MARGIN;
  const top = HEADER_TOP;

  const brandW = 26;
  const brandH = 14;

  const tenantW = 26;
  const tenantH = 14;

  // Logos (best-effort)
  try {
    if (!cachedBrandLogoDataUrl) cachedBrandLogoDataUrl = await loadImageAsDataUrl(brandLogo);
    safeAddImage(doc, cachedBrandLogoDataUrl, mx, top, brandW, brandH);
  } catch {}

  try {
    const t = String(tenantLogoUrl || '').trim();
    if (t) {
      if (!cachedTenantLogoDataUrl) cachedTenantLogoDataUrl = await loadImageAsDataUrl(t);
      safeAddImage(doc, cachedTenantLogoDataUrl, pageWidth - mx - tenantW, top, tenantW, tenantH);
    }
  } catch {}

  // Report title (header only, reduced size)
  setText(doc, BRAND_ORANGE);
  doc.setFontSize(12);
  doc.setFont(FONT_FAMILY as any, 'bold');
  doc.text(reportTitle, mx, top + brandH + 10);

  // Divider line
  setDraw(doc, '#E6E6E6');
  doc.line(mx, top + HEADER_H, pageWidth - mx, top + HEADER_H);

  // Reset body defaults
  setText(doc, TEXT_COLOR);
  doc.setFontSize(BODY_SIZE);
  doc.setFont(FONT_FAMILY as any, 'normal');
}


/* ======================================================
   Page Utilities
   ====================================================== */

async function addNewPageWithHeader(
  pageNo: number,
  brandLogo: string,
  tenantLogoUrl: string | undefined,
  reportTitle: string
) {
  doc.addPage();
  doc.setPage(pageNo);
  await drawHeader(pageNo, reportTitle, brandLogo, tenantLogoUrl);
  yCursor = BODY_START_Y;
}

async function checkPageBreak(
  spaceNeeded: number,
  brandLogo: string,
  tenantLogoUrl: string | undefined,
  reportTitle: string,
  pageNoRef: { n: number }
) {
  if (yCursor + spaceNeeded > pageHeight - MARGIN) {
    pageNoRef.n += 1;

    doc.addPage();
    doc.setPage(pageNoRef.n);

    await drawHeader(pageNoRef.n, reportTitle, brandLogo, tenantLogoUrl);

    // HARD BOUNDARY: body always starts below header on new pages
    yCursor = BODY_START_Y;
  }
}


function addFooter(tenantLogoUrl?: string) {
  const totalPages = (doc as any).internal.getNumberOfPages();

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);

    doc.setFontSize(FOOTER_SIZE);
    doc.setFont(FONT_FAMILY as any, 'normal');
    setText(doc, FOOTER_GREY);

    doc.text('© 2026 One Size Fits None™', MARGIN, pageHeight - 10);
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - MARGIN, pageHeight - 10, {
      align: 'right',
    });

    // Optional: tiny tenant logo in footer-left (only if already cached)
    try {
      if (cachedTenantLogoDataUrl) {
        safeAddImage(doc, cachedTenantLogoDataUrl, MARGIN, pageHeight - 18, 10, 10);
      }
    } catch {
      // ignore
    }
  }
}

/* ======================================================
   Preprocessing
   ====================================================== */

function preprocessRawText(raw: string): string {
  if (!raw) return '';

  let t = raw;

  t = t.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  t = t.replace(/<\/?center>/gi, '');
  t = t.replace(/\*\*/g, '');
  t = t.replace(/__/g, '');
  t = t.replace(/\$([^$]+)\$/g, '$1');

  t = t.replace(/^\s*\*\s+/gm, '• ');
  t = t.replace(/^\s*-\s+/gm, '• ');

  t = t.replace(/^\s*Page\s+\d+(\s+of\s+\d+)?\s*$/gmi, '');
  t = t.replace(/^\s*©\s*\d{4}\s*One\s*Size\s*Fits\s*None.*$/gmi, '');

  t = t.replace(/^\s*SECTION\s+(\d+)\s*:\s*(.+)$/gmi, (_m, n, title) => `${n}. ${title.trim()}`);
  t = t.replace(/^\s*(\d+)\)\s*(.+)$/gm, (_m, n, title) => `${n}. ${title.trim()}`);

  const key = 'STUDENT DIAGNOSTIC SYNOPSIS';
  const upper = t.toUpperCase();
  const first = upper.indexOf(key);
  if (first !== -1) {
    const second = upper.indexOf(key, first + key.length);
    if (second !== -1) {
      t = t.slice(0, second).trim();
    }
  }

  t = t.replace(/\n{3,}/g, '\n\n');

  return t.trim();
}

/* ======================================================
   Title Page
   ====================================================== */

async function addTitlePage(
  inputs: AnalysisInputSummary,
  brandLogo: string,
  tenantLogoUrl: string | undefined,
  reportTitle: string,
  pageNoRef: { n: number }
) {
  // Title page is page 1
  pageNoRef.n = 1;
  await drawHeader(1, reportTitle, brandLogo, tenantLogoUrl);

  yCursor = pageHeight / 3.5;

  setText(doc, BRAND_GREY);
  doc.setFontSize(24);
  // no font switching
doc.setFont(FONT_FAMILY, 'bold');

  doc.text('A-Typical™ Diagnostic Report', pageWidth / 2, yCursor, { align: 'center' });

  // reset to normal
doc.setFont(FONT_FAMILY as any, 'normal');
  yCursor += 25;

  const boxWidth = 120;
  const boxX = (pageWidth - boxWidth) / 2;

  setDraw(doc, '#DDDDDD');
  setFill(doc, '#FFFFFF');
  doc.roundedRect(boxX, yCursor, boxWidth, 40, 3, 3, 'FD');

  const lx = boxX + 10;
  const vx = boxX + 45;

  doc.setFontSize(BODY_SIZE);
doc.setFont(FONT_FAMILY, 'bold');

  setText(doc, TEXT_COLOR);
  doc.text('Student:', lx, yCursor + 10);
  doc.text('Grade:', lx, yCursor + 20);
  doc.text('Subject:', lx, yCursor + 30);

doc.setFont(FONT_FAMILY as any, 'normal');
  // Print exactly what UI passed. No fallback to "All" here.
  doc.text(String(inputs.studentName || ''), vx, yCursor + 10);
  doc.text(String(inputs.grade || ''), vx, yCursor + 20);
  doc.text(String(inputs.subject || ''), vx, yCursor + 30);
}

/* ======================================================
   Content Renderer
   ====================================================== */

async function renderContent(
  rawText: string,
  brandLogo: string,
  tenantLogoUrl: string | undefined,
  reportTitle: string,
  pageNoRef: { n: number }
) {
  const text = preprocessRawText(rawText);

  // Start content on page 2
  pageNoRef.n += 1;
  await addNewPageWithHeader(pageNoRef.n, brandLogo, tenantLogoUrl, reportTitle);

  doc.setFontSize(BODY_SIZE);
  doc.setFont(undefined as any, 'normal');
  setText(doc, TEXT_COLOR);
  doc.setLineHeightFactor(LINE_HEIGHT_FACTOR);

  const lines = text.split('\n');

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    const next = (lines[i + 1] || '').trim();
    if (!line) continue;

    const bodyLH = ptToMm(BODY_SIZE * LINE_HEIGHT_FACTOR);

// H1
if (line === 'STUDENT DIAGNOSTIC SYNOPSIS') {
  const h1LH = ptToMm(H1_SIZE * LINE_HEIGHT_FACTOR);

  // Find next non-empty line to keep with heading
  let j = i + 1;
  while (j < lines.length && !String(lines[j] || '').trim()) j++;
  const nextLine = j < lines.length ? String(lines[j] || '').trim() : '';

  // Estimate first-body height
  let firstBodyHeight = bodyLH * 3; // fallback
  if (nextLine) {
    if (nextLine.startsWith('• ')) {
      const txt = nextLine.substring(2);
      const bodyLines = doc.splitTextToSize(txt, pageWidth - MARGIN * 2 - 6);
      firstBodyHeight = Math.max(bodyLH, bodyLines.length * bodyLH);
    } else if (/^\d+\.\s+/.test(nextLine)) {
      firstBodyHeight = bodyLH * 3;
    } else {
      const bodyLines = doc.splitTextToSize(nextLine, pageWidth - MARGIN * 2);
      firstBodyHeight = Math.max(bodyLH, bodyLines.length * bodyLH);
    }
  }

  // Include spacing you apply before/after heading
  const required = (PARA_SPACING * 2) + h1LH + PARA_SPACING + firstBodyHeight;

  await checkPageBreak(required, brandLogo, tenantLogoUrl, reportTitle, pageNoRef);

  yCursor += PARA_SPACING * 2;

  doc.setFontSize(H1_SIZE);
  doc.setFont(FONT_FAMILY as any, 'bold');
  setText(doc, BRAND_ORANGE);
  doc.text(line, MARGIN, yCursor);

  setText(doc, TEXT_COLOR);
  doc.setFontSize(BODY_SIZE);
  doc.setFont(FONT_FAMILY as any, 'normal');

  yCursor += h1LH + PARA_SPACING;
  continue;
}

   // H2 (numbered sections)
if (/^\d+\.\s+/.test(line)) {
  const h2LH = ptToMm(H2_SIZE * LINE_HEIGHT_FACTOR);

  // Measure heading height (handles multi-line headings)
  doc.setFontSize(H2_SIZE);
  doc.setFont(FONT_FAMILY as any, 'bold');
  const headingLines = doc.splitTextToSize(line, pageWidth - MARGIN * 2);
  const headingHeight = headingLines.length * h2LH;

  // Find the next non-empty content line to keep with the heading
  let j = i + 1;
  while (j < lines.length && !String(lines[j] || '').trim()) j++;
  const nextLine = j < lines.length ? String(lines[j] || '').trim() : '';

  // Estimate first-body height (bullet/paragraph)
  let firstBodyHeight = bodyLH * 3; // fallback
  if (nextLine) {
    if (nextLine.startsWith('• ')) {
      const txt = nextLine.substring(2);
      const bodyLines = doc.splitTextToSize(txt, pageWidth - MARGIN * 2 - 6);
      firstBodyHeight = Math.max(bodyLH, bodyLines.length * bodyLH);
    } else if (/^\d+\.\s+/.test(nextLine) || nextLine === 'STUDENT DIAGNOSTIC SYNOPSIS') {
      firstBodyHeight = bodyLH * 3; // next is another heading; still force space
    } else {
      const bodyLines = doc.splitTextToSize(nextLine, pageWidth - MARGIN * 2);
      firstBodyHeight = Math.max(bodyLH, bodyLines.length * bodyLH);
    }
  }

  // Include the spacing you add BEFORE and AFTER the heading
  const required =
    (PARA_SPACING * 1.5) + headingHeight + PARA_SPACING + firstBodyHeight;

  await checkPageBreak(required, brandLogo, tenantLogoUrl, reportTitle, pageNoRef);

  // Now apply spacing AFTER the page-break check (important)
  yCursor += PARA_SPACING * 1.5;

  setText(doc, BRAND_ORANGE);
  doc.setFontSize(H2_SIZE);
  doc.setFont(FONT_FAMILY as any, 'bold');
  doc.text(headingLines, MARGIN, yCursor);

  yCursor += headingHeight + PARA_SPACING;

  setText(doc, TEXT_COLOR);
  doc.setFontSize(BODY_SIZE);
  doc.setFont(FONT_FAMILY as any, 'normal');
  continue;
}


    // Bullet
    if (line.startsWith('• ')) {
      const txt = line.substring(2);
      const split = doc.splitTextToSize(txt, pageWidth - MARGIN * 2 - 6);
      await checkPageBreak(split.length * bodyLH, brandLogo, tenantLogoUrl, reportTitle, pageNoRef);

      doc.text('•', MARGIN, yCursor);
      doc.text(split, MARGIN + 6, yCursor);
      yCursor += split.length * bodyLH;

      if (!next.startsWith('• ')) yCursor += PARA_SPACING;
      continue;
    }

    // Paragraph
    const split = doc.splitTextToSize(line, pageWidth - MARGIN * 2);
    await checkPageBreak(split.length * bodyLH, brandLogo, tenantLogoUrl, reportTitle, pageNoRef);

    doc.text(split, MARGIN, yCursor);
    yCursor += split.length * bodyLH;

    if (!next || next.startsWith('• ') || /^\d+\.\s+/.test(next) || next === 'STUDENT DIAGNOSTIC SYNOPSIS') {
      yCursor += PARA_SPACING;
    }
  }
}

/* ======================================================
   Export
   ====================================================== */

export async function generateATypicalPdf(
  rawText: string,
  inputs: AnalysisInputSummary,
  brandLogo: string,
  partnerLogo: string, // retained for compatibility; not used
  tenantLogoUrl?: string
) {
  void partnerLogo;

  cachedBrandLogoDataUrl = null;
  cachedTenantLogoDataUrl = null;

  doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
  pageHeight = doc.internal.pageSize.height;
  pageWidth = doc.internal.pageSize.width;
  yCursor = BODY_START_Y;

  FONT_FAMILY = ensureRaleway(doc) ? 'Raleway' : 'helvetica';
doc.setFont(FONT_FAMILY as any, 'normal');

  const reportTitle = 'A-Typical Diagnostic Report';
  const pageNoRef = { n: 1 };

  await addTitlePage(inputs, brandLogo, tenantLogoUrl, reportTitle, pageNoRef);
  await renderContent(rawText, brandLogo, tenantLogoUrl, reportTitle, pageNoRef);

  addFooter(tenantLogoUrl);

  doc.save(
    `A-Typical-Report-${String(inputs.studentName || 'Student').replace(/\s/g, '_')}.pdf`
  );
}
